import * as React from 'react';
import {EventMatchLabel} from 'Src/components/EventMatchLabel/EventMatchLabel';
import propz from 'propz';
import moment from 'moment';
import { SchoolEvent } from 'Src/models/event';
import { getActiveSchoolIdForParent, getEventNameForParent } from 'Src/helpers/accessor/accessor';
import { getSignEventType, getEventResultForCalendar } from 'Src/helpers/calendar/calendar';
import { Child } from 'Src/models/child';
import { AppUser } from 'Src/views/App/App';
import { getCalendarEventVisualStatus } from 'Src/helpers/calendar/eventVisualStatus';

interface Props {
  user: AppUser;
  events: SchoolEvent[];
  children: Child[];
  onEventClick: (eventId: string) => void;
  childColors?: Record<string, string>;
}

const TYPE_DETAILS = {
  F: { short: 'Fixture', className: 'fixture', icon: 'fixture' },
  C: { short: 'Club', className: 'club', icon: 'club' },
  T: { short: 'Tournament event', className: 'tournamentEvent', icon: 'trophy' }
};

function renderCalendarListIcon(icon: string) {
  switch (icon) {
    case 'club':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3 5 7v10l7 4 7-4V7l-7-4z" />
          <path d="M5 7l7 4 7-4" />
          <path d="M12 11v10" />
        </svg>
      );

    case 'trophy':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
          <path d="M8 5H5a2 2 0 0 0 2 4h1" />
          <path d="M16 5h3a2 2 0 0 1-2 4h-1" />
          <path d="M12 11v4" />
          <path d="M9 20h6" />
        </svg>
      );

    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 3v3M16 3v3M4 9h16" />
          <rect x="4" y="5" width="16" height="15" rx="3" />
        </svg>
      );
  }
}

export function EventAndTournamentsTable(props: Props) {
  const { events, children, onEventClick, childColors = {} } = props;
  const childIdList = children.map(child => child.id);

  if (events.length === 0) {
    return (
      <div className="cal-empty">
        <div className="cal-empty__icon">{renderCalendarListIcon('fixture')}</div>
        <div className="cal-empty__title">Nothing scheduled</div>
        <div className="cal-empty__text">No events on this day for the selected filter.</div>
      </div>
    );
  }

  return (
    <div className="cal-events">
      {events.map((event, index) => {
        const activeSchoolId = getActiveSchoolIdForParent(event, childIdList);
        const sign = getSignEventType(event);
        const type = TYPE_DETAILS[sign] || TYPE_DETAILS.F;
        const result = getEventResultForCalendar(event, activeSchoolId);
        const visualStatus = getCalendarEventVisualStatus(event, activeSchoolId);
        const eventPlayers = propz.get(event, ['players'], []);
        const eventPlayersIds = eventPlayers.map(player => player.userId);
        const childrenInEvent = children.filter(
          (child, childIndex, list) =>
            eventPlayersIds.some(playerId => String(playerId) === String(child.id)) &&
            list.findIndex(candidate => String(candidate.id) === String(child.id)) === childIndex
        );
        const primaryChild = childrenInEvent[0];
        const primaryColor = primaryChild ? childColors[primaryChild.id] || '#227ebd' : '#227ebd';

        return (
          <button
            type="button"
            onClick={() => onEventClick(event.id)}
            key={`event_${index}`}
            className={`cal-ev cal-ev--${type.className} ${visualStatus.eventClassName}`}
          >
            <span className="cal-ev__time">{moment(event.startTime).format('HH:mm')}</span>
            <span className="cal-ev__bar" style={{ background: primaryColor }} aria-hidden="true" />
            <span className="cal-ev__children" aria-hidden="true">
              {childrenInEvent.map(child => {
                const childAvatar = propz.get(child, ['avatar'], '');
                const isAvatarUrlExist = childAvatar !== '';
                const firstNameInitial = child.firstName.substring(0, 1);
                const childColor = childColors[child.id] || primaryColor;

                return isAvatarUrlExist ? (
                  <span
                    key={child.id}
                    className="cal-childAvatar"
                    style={{ backgroundImage: `url(${childAvatar})`, backgroundColor: childColor }}
                  />
                ) : (
                  <span key={child.id} className="cal-childAvatar" style={{ background: childColor }}>
                    {firstNameInitial}
                  </span>
                );
              })}
            </span>
            {childrenInEvent.length === 0 ? (
              <span className="cal-ev__ico" aria-hidden="true">{renderCalendarListIcon(type.icon)}</span>
            ) : null}
            <span className="cal-ev__main">
              <span className="cal-ev__name">{getEventNameForParent(event, childIdList)}</span>
              <EventMatchLabel event={event} />
              <span className="cal-ev__meta">
                {primaryChild ? <span className="pcal-ev__kid" style={{ color: primaryColor }}>{primaryChild.firstName}</span> : null}
                {primaryChild ? <span className="pcal-ev__sep">·</span> : null}
                <span className="pcal-ev__type">{type.short}</span>
                {result && result !== '-' ? <span> · {result}</span> : null}
              </span>
            </span>
            <span className="cal-ev__right">
              <span className={`cal-badge ${visualStatus.badgeClassName}`}>{visualStatus.label}</span>
              <span className="cal-ev__chev" aria-hidden="true">›</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
